import { CANS_PER_KEG, FORMAT_DEFINITIONS } from './constants';

export const isCountableUnit = (unit, category) => {
    return unit === 'S' || category === 'OTHER';
};

export const standardizeToBaseUnit = (value, unit) => {
    if (value == null) return 0;

    const unitUpper = unit?.toUpperCase() || '';

    if (unitUpper === 'MG') return value * 0.001;
    if (unitUpper === 'KG') return value * 1000;
    if (unitUpper === 'GRAMS') return value;
    if (unitUpper === 'LITERS') return value * 1000;
    if (unitUpper === 'ML') return value;
    if (unitUpper === 'S' || unitUpper === 'UNITS' || unitUpper === 'OTHER') return value;

    return value;
};

export const getEffectiveRequiredPerKeg = (ingredient, cansPerKeg) => {
    if (isCountableUnit(ingredient.ingredient_unit, ingredient.ingredient_category)) {
        return cansPerKeg;
    }

    return ingredient.standardized_required ||
        standardizeToBaseUnit(ingredient.required_per_keg, ingredient.ingredient_unit);
};

export const buildBottleneckIngredient = (bottleneck) => ({
    id: bottleneck.ingredient_id,
    name: bottleneck.ingredient_name,
    available_quantity: bottleneck.available_quantity,
    required_quantity: bottleneck.required_per_keg,
    unit: bottleneck.ingredient_unit,
    standardized_available: bottleneck.standardized_available,
    standardized_required: bottleneck.standardized_required,
    base_unit: bottleneck.base_unit
});

export const buildInitialScenarioData = (productionCapacityData) => {
    const inventory = {};
    const cansPerKeg = productionCapacityData.conversion_rate?.cans_per_keg || CANS_PER_KEG;

    const processedData = {
        ...productionCapacityData,
        products_with_capacity: productionCapacityData.products_with_capacity.map(product => ({
            ...product,
            cans_per_keg: cansPerKeg,
            ingredient_analysis: product.ingredient_analysis.map(ingredient => {
                inventory[ingredient.ingredient_id] = ingredient.available_quantity;

                return {
                    ...ingredient,
                    effective_required_per_keg: ingredient.standardized_required ||
                        getEffectiveRequiredPerKeg(ingredient, cansPerKeg)
                };
            })
        }))
    };

    processedData.products_with_capacity = processedData.products_with_capacity.map(product => {
        const bottleneck = product.ingredient_analysis && product.ingredient_analysis.length > 0
            ? product.ingredient_analysis.reduce((min, curr) =>
                curr.max_cans_possible < min.max_cans_possible ? curr : min
              )
            : {
                ingredient_id: 'no-ingredients',
                ingredient_name: 'No Ingredients',
                available_quantity: 0,
                required_per_keg: 0,
                ingredient_unit: 'units',
                standardized_available: 0,
                standardized_required: 0,
                base_unit: 'units',
                max_cans_possible: 0,
                max_kegs_possible: 0
              };

        return {
            ...product,
            production_capacity_cans: bottleneck.max_cans_possible,
            bottleneck_ingredient: buildBottleneckIngredient(bottleneck)
        };
    });

    return { processedData, inventory };
};

export const recalculateScenarioCapacity = (originalData, updatedInventory) => {
    if (!originalData) return null;

    const updatedProducts = originalData.products_with_capacity.map(product => {
        const cansPerKeg = product.cans_per_keg || CANS_PER_KEG;

        const updatedAnalysis = product.ingredient_analysis.map(ingredient => {
            const newQty = updatedInventory[ingredient.ingredient_id] || ingredient.available_quantity;
            const standardizedAvailable = standardizeToBaseUnit(newQty, ingredient.ingredient_unit);
            let standardizedRequired = standardizeToBaseUnit(ingredient.required_per_keg, ingredient.ingredient_unit);

            if (isCountableUnit(ingredient.ingredient_unit, ingredient.ingredient_category)) {
                standardizedRequired = cansPerKeg;
            }

            const maxKegs = standardizedAvailable / standardizedRequired;
            const maxCans = Math.floor(maxKegs * cansPerKeg);

            return {
                ...ingredient,
                available_quantity: newQty,
                standardized_available: standardizedAvailable,
                standardized_required: standardizedRequired,
                effective_required_per_keg: standardizedRequired,
                max_kegs_possible: maxKegs,
                max_cans_possible: maxCans
            };
        });

        const bottleneck = updatedAnalysis && updatedAnalysis.length > 0
            ? updatedAnalysis.reduce((min, curr) =>
                curr.max_cans_possible < min.max_cans_possible ? curr : min
              )
            : {
                ingredient_id: 'no-ingredients',
                ingredient_name: 'No Ingredients',
                available_quantity: 0,
                required_per_keg: 0,
                ingredient_unit: 'units',
                standardized_available: 0,
                standardized_required: 0,
                base_unit: 'units',
                max_cans_possible: 0,
                max_kegs_possible: 0
              };

        return {
            ...product,
            ingredient_analysis: updatedAnalysis,
            production_capacity_cans: bottleneck.max_cans_possible,
            bottleneck_ingredient: buildBottleneckIngredient(bottleneck)
        };
    });

    return {
        ...originalData,
        products_with_capacity: updatedProducts
    };
};

export const buildInventoryFromScenarioData = (scenarioData) => {
    const inventory = {};

    scenarioData.products_with_capacity.forEach(product => {
        product.ingredient_analysis.forEach(ingredient => {
            inventory[ingredient.ingredient_id] = ingredient.available_quantity;
        });
    });

    return inventory;
};

export const getAvailableConcentrateKegs = (product, finishedKegs = {}) => {
    const fromIngredients = product.production_capacity_cans / CANS_PER_KEG;
    const fromFinished = finishedKegs[product.product_id] || 0;

    return fromIngredients + fromFinished;
};

export const getTotalAllocatedKegs = (formatAllocations = {}, productId) => {
    const allocations = formatAllocations[productId] || {};

    return Object.values(allocations).reduce((sum, kegs) => sum + kegs, 0);
};

// How many concentrate kegs are still free for this format, given whatever
// has already been explicitly allocated to the product's other formats.
export const getRemainingKegsForFormat = (formatAllocations = {}, productId, formatId, availableKegs) => {
    const currentAllocations = formatAllocations[productId] || {};
    const otherAllocations = Object.entries(currentAllocations)
        .filter(([currentFormatId]) => currentFormatId !== formatId)
        .reduce((sum, [, allocatedKegs]) => sum + allocatedKegs, 0);

    return Math.max(0, availableKegs - otherAllocations);
};

// The quantity that's actually "set" for a format right now — an explicit
// user entry if there is one, otherwise the same default the input pre-fills
// with (capacity mode) or 0 (target mode, which requires an explicit target).
// Single source of truth so the value driving "can I create a plan" always
// matches what the quantity field visibly shows.
export const getQuantityValueForFormat = (viewMode, formatAllocations, targetAllocations, productId, formatId, availableKegs) => {
    if (viewMode === 'target') {
        return targetAllocations[productId]?.[formatId] || 0;
    }

    const explicit = formatAllocations[productId]?.[formatId];
    return explicit !== undefined
        ? explicit
        : getRemainingKegsForFormat(formatAllocations, productId, formatId, availableKegs);
};

export const calculateFormatOutput = (concentrateKegs, formatId) => {
    const format = FORMAT_DEFINITIONS[formatId];
    const units = concentrateKegs * format.per_concentrate_keg;
    const wsRevenue = units * format.pricing.ws;
    const retailRevenue = units * format.pricing.retail;

    return { units, wsRevenue, retailRevenue, format };
};

export const getProductTotalRevenue = (formatAllocations = {}, productId) => {
    const allocations = formatAllocations[productId] || {};

    return Object.entries(allocations).reduce((totals, [formatId, kegs]) => {
        const { wsRevenue, retailRevenue } = calculateFormatOutput(kegs, formatId);

        return {
            ws: totals.ws + wsRevenue,
            retail: totals.retail + retailRevenue
        };
    }, { ws: 0, retail: 0 });
};

export const buildProductionRunsPayload = ({
    displayedProducts,
    formatAllocations,
    planStartDate
}) => {
    return displayedProducts.flatMap(product => {
        const allocations = formatAllocations[product.product_id] || {};
        const positiveAllocations = Object.entries(allocations).filter(([, kegs]) => kegs > 0);

        if (positiveAllocations.length > 0) {
            return positiveAllocations.map(([formatId, kegs]) => {
                const { units, format } = calculateFormatOutput(kegs, formatId);
                const plannedQuantity = Math.floor(units);

                return {
                    product_id: product.product_id,
                    planned_quantity: plannedQuantity,
                    output_format: formatId,
                    output_format_name: format.name,
                    output_unit: format.unit,
                    concentrate_kegs: kegs,
                    unit_wholesale_price: format.pricing.ws,
                    unit_retail_price: format.pricing.retail,
                    planned_date: new Date(planStartDate).toISOString(),
                    notes: `${product.product_name} - ${plannedQuantity} ${format.unit} (${kegs.toFixed(2)} concentrate kegs)`
                };
            });
        }

        const defaultFormat = FORMAT_DEFINITIONS.cans_rtd;

        return [{
            product_id: product.product_id,
            planned_quantity: product.production_capacity_cans,
            output_format: defaultFormat.id,
            output_format_name: defaultFormat.name,
            output_unit: defaultFormat.unit,
            concentrate_kegs: product.production_capacity_cans / defaultFormat.per_concentrate_keg,
            unit_wholesale_price: defaultFormat.pricing.ws,
            unit_retail_price: defaultFormat.pricing.retail,
            planned_date: new Date(planStartDate).toISOString(),
            notes: `${product.product_name} - ${product.production_capacity_cans} cans`
        }];
    });
};

export const getProductTargetRevenue = (targetAllocations = {}, productId) => {
    const allocations = targetAllocations[productId] || {};

    return Object.entries(allocations).reduce((totals, [formatId, targetUnits]) => {
        const format = FORMAT_DEFINITIONS[formatId];

        return {
            ws: totals.ws + (targetUnits * format.pricing.ws),
            retail: totals.retail + (targetUnits * format.pricing.retail)
        };
    }, { ws: 0, retail: 0 });
};

export const getTargetKegsNeeded = (targetAllocations = {}, productId) => {
    const allocations = targetAllocations[productId] || {};

    return Object.entries(allocations).reduce((sum, [formatId, targetUnits]) => {
        const format = FORMAT_DEFINITIONS[formatId];
        return sum + (targetUnits / format.per_concentrate_keg);
    }, 0);
};

// For the entered target, work out which ingredient(s) can't keep up, net of
// concentrate/finished kegs already on hand (which cover part of the target
// without needing more of any ingredient).
export const getTargetBottleneck = (product, targetAllocations, finishedKegs = {}) => {
    const neededKegs = getTargetKegsNeeded(targetAllocations, product.product_id);

    if (neededKegs <= 0) {
        return { neededKegs: 0, neededFromIngredients: 0, isFeasible: true, limitingIngredients: [] };
    }

    const bufferKegs = finishedKegs[product.product_id] || 0;
    const neededFromIngredients = Math.max(0, neededKegs - bufferKegs);

    const limitingIngredients = product.ingredient_analysis
        .filter(ingredient => ingredient.max_kegs_possible < neededFromIngredients)
        .map(ingredient => ({
            ingredient_id: ingredient.ingredient_id,
            ingredient_name: ingredient.ingredient_name,
            shortfallKegs: neededFromIngredients - ingredient.max_kegs_possible
        }))
        .sort((a, b) => b.shortfallKegs - a.shortfallKegs);

    return {
        neededKegs,
        neededFromIngredients,
        isFeasible: limitingIngredients.length === 0,
        limitingIngredients
    };
};

export const buildTargetProductionRunsPayload = ({
    displayedProducts,
    targetAllocations,
    planStartDate
}) => {
    return displayedProducts.flatMap(product => {
        const allocations = targetAllocations[product.product_id] || {};

        return Object.entries(allocations)
            .filter(([, targetUnits]) => targetUnits > 0)
            .map(([formatId, targetUnits]) => {
                const format = FORMAT_DEFINITIONS[formatId];
                const concentrateKegs = targetUnits / format.per_concentrate_keg;
                const plannedQuantity = Math.floor(targetUnits);

                return {
                    product_id: product.product_id,
                    planned_quantity: plannedQuantity,
                    output_format: formatId,
                    output_format_name: format.name,
                    output_unit: format.unit,
                    concentrate_kegs: concentrateKegs,
                    unit_wholesale_price: format.pricing.ws,
                    unit_retail_price: format.pricing.retail,
                    planned_date: new Date(planStartDate).toISOString(),
                    notes: `${product.product_name} - ${plannedQuantity} ${format.unit} (${concentrateKegs.toFixed(2)} concentrate kegs)`
                };
            });
    });
};
